import type { ComponentPropsWithoutRef } from 'react';
import { cx } from '@/lib/cx';
import { Icon, type IconName } from './Icon';
import styles from './Button.module.css';

type Variant = 'primary' | 'secondary' | 'ghost';

interface CommonProps {
  variant?: Variant;
  size?: 'md' | 'lg';
  /** Trailing icon; nudges forward on hover. */
  icon?: IconName;
  block?: boolean;
}

type AnchorProps = CommonProps & ComponentPropsWithoutRef<'a'> & { href: string };
type NativeButtonProps = CommonProps & ComponentPropsWithoutRef<'button'> & { href?: undefined };

export type ButtonProps = AnchorProps | NativeButtonProps;

/**
 * One button, two elements: pass `href` and it renders a semantic anchor,
 * omit it and you get a real `<button>`. Never a div pretending to be either.
 */
export function Button({ variant = 'primary', size = 'md', icon, block, className, children, ...rest }: ButtonProps) {
  const classes = cx(styles.base, styles[variant], size === 'lg' && styles.lg, block && styles.block, className);

  const content = (
    <>
      {children}
      {icon && <Icon name={icon} size={size === 'lg' ? 20 : 18} className={styles.icon} />}
    </>
  );

  if (typeof rest.href === 'string') {
    const { href, ...anchorProps } = rest as AnchorProps;
    return (
      <a href={href} className={classes} {...anchorProps}>
        {content}
      </a>
    );
  }

  const { type = 'button', ...buttonProps } = rest as NativeButtonProps;
  return (
    <button type={type} className={classes} {...buttonProps}>
      {content}
    </button>
  );
}
